import { formatQuoteRef } from '@misumi/shared'
import { type ReactNode, useState } from 'react'
import { Card } from '../../components/ui/Card'
import { formatOrderRef } from '../checkout/checkout-domain'
import { ActivityLogPanel } from './ActivityLogPanel'
import type { Order } from './domain'
import {
  computeOrderTotal,
  formatDate,
  formatMoney,
  isImportCustoms,
  resolveOrderPaidBy,
} from './domain'

export interface OrderSummaryCardProps {
  readonly order: Order
}

/**
 * Order-detail summary card (UX spec §2.2 item 2), reconciled to the
 * prototype's own layout (`Fictiv Platform.dc.html`, "ORDER DETAIL" §, the
 * `<!-- summary card -->` block, ~L3271-3285 — no screenshot exists for this
 * screen, so the prototype markup is the pixel source of truth):
 *
 * - A primary row of big ("Order ID" / "Order total" / "Order placed")
 *   stats, plus a right-aligned "View activity log" link — NOT a vertical
 *   dt/dd list, as the earlier build had it.
 * - A second row below a hairline divider: a grid of secondary fields
 *   (Shipping address / Shipping method / Reference quote / Payment method /
 *   Order paid by / Shipping terms).
 *
 * S6/UID-100 (D2): shipping address and payment method are now REAL,
 * frozen-at-placement snapshot fields on `Order` (`shippingAddress` /
 * `payment`, ADR-0038 §1) — legitimately `null` for a pre-checkout/pre-UID-60
 * order, not a contract gap anymore. "Order paid by" (recipient name) and
 * "Reference quote #" remain genuinely unbacked — no field exists for
 * either — and keep their documented fallbacks.
 *
 * UID-123 (S8 design-fidelity round 2, item 2): the secondary grid is
 * reconciled to the prototype's actual 5-column layout
 * (`Fictiv Platform.dc.html:3318-3322`) — Shipping address / Reference
 * quote # / Payment method / Order paid by / Shipping terms, in that order.
 * `Shipping method` and `PO reference` (added by S6/UID-100 as a plausible
 * superset) are NOT in the prototype's secondary grid at all — dropped here
 * to match pixel-fidelity; they remain real Order fields, just not surfaced
 * on this card. "Shipping terms" is derived from `order.customs.incoterms`
 * (a single-value `"DDP"` enum today, ADR per CustomsInfo doc comment) — the
 * prototype's literal copy is "DDP (Fictiv is importer)", so that suffix is
 * a fixed label for the one enum value that exists, not fabricated data;
 * renders "—" when the order has no customs record.
 *
 * "View activity log" is LIVE (UID-121 S7): `order.activityLog` is real
 * backend data, so the button opens the `ActivityLogPanel` modal rather than
 * staying disabled — the empty state (`[]`) renders inside that panel.
 */
export function OrderSummaryCard({ order }: OrderSummaryCardProps) {
  const total = computeOrderTotal(order)
  const [activityLogOpen, setActivityLogOpen] = useState(false)

  return (
    <Card className="order-summary-card">
      <div className="order-summary-primary-row">
        <dl className="order-summary-primary-item">
          <div>
            <dt>Order ID</dt>
            {/* UID-317: design (`Fictiv Platform.dc.html:3878`) shows the
            friendly "SO-#####" reference ONLY — no raw id underneath. */}
            <dd>
              <span className="order-id-ref">{formatOrderRef(order.id)}</span>
            </dd>
          </div>
        </dl>
        <dl className="order-summary-primary-item">
          <div>
            <dt>Order total</dt>
            <dd>{formatMoney(total.amount, total.currency)}</dd>
          </div>
        </dl>
        <dl className="order-summary-primary-item order-summary-primary-item-wide">
          <div>
            <dt>Order placed</dt>
            <dd>{formatDate(order.placedAt)}</dd>
          </div>
        </dl>
        {/* UID-121 S7: `order.activityLog` is now real backend data — opens
        the ActivityLogPanel modal instead of staying disabled. */}
        <div className="order-summary-activity">
          <button type="button" className="btn-link" onClick={() => setActivityLogOpen(true)}>
            View activity log
          </button>
        </div>
      </div>
      <div className="order-summary-detail-row">
        <dl>
          <div>
            <dt>Shipping address</dt>
            <dd>
              {formatShippingAddress(order.shippingAddress, resolveOrderPaidBy(order) ?? null)}
            </dd>
          </div>
        </dl>
        <dl>
          <div>
            <dt>Reference quote #</dt>
            {/* UID-212: OrderLine now carries the as-placed sourceQuoteId
            lineage (migration 3012), so this shows the real originating quote
            reference derived across the order's lines — the honest "None"
            fallback stays for a catalog-only / pre-3012 order that genuinely
            has no originating quote. */}
            <dd>{formatReferenceQuote(order.lines)}</dd>
          </div>
        </dl>
        <dl>
          <div>
            <dt>Payment method</dt>
            <dd>{formatPaymentMethod(order.payment)}</dd>
          </div>
        </dl>
        <dl>
          <div>
            <dt>Order paid by</dt>
            {/* UID-130 follow-up: the contract's PaymentSelection carries no
            payer name (deliberate PCI boundary — no cardholder data) and the
            frozen shipping snapshot drops the contact name, so the buyer
            identity is read from the one place it legitimately lives on the
            aggregate — the actor of the customer-authored `placed` entry in
            the real backend activity log (resolveOrderPaidBy). Falls back to
            the same graceful "—" the other unpopulated fields use when no
            such entry/actor exists. */}
            <dd className="caption-text">{resolveOrderPaidBy(order) ?? '—'}</dd>
          </div>
        </dl>
        <dl>
          <div>
            <dt>Shipping terms</dt>
            <dd>{formatShippingTerms(order.customs)}</dd>
          </div>
        </dl>
      </div>
      <ActivityLogPanel
        order={order}
        open={activityLogOpen}
        onClose={() => setActivityLogOpen(false)}
      />
    </Card>
  )
}

/** S6/UID-100 (D2): renders the frozen as-placed `ShippingAddress` snapshot, or a
 * graceful "—" for the legitimately-nullable pre-checkout/pre-UID-60 case.
 * UID-317 review follow-up: lead with the recipient name (the design's Shipping
 * address block is `paidBy` over the address lines, `Fictiv Platform.dc.html:3884`).
 * `recipientName` is the same resolved "Order paid by" buyer identity — the frozen
 * shipping snapshot itself carries no contact name (documented contract gap), so we
 * reuse the one name the aggregate does expose, exactly as the mock does. Omitted
 * when unknown. */
function formatShippingAddress(
  address: Order['shippingAddress'],
  recipientName: string | null,
): ReactNode {
  if (!address) {
    return <span className="caption-text">—</span>
  }
  return (
    <>
      {recipientName && (
        <>
          <span>{recipientName}</span>
          <br />
        </>
      )}
      <span>{address.line1}</span>
      {address.line2 && <span>, {address.line2}</span>}
      <br />
      <span>
        {address.city}
        {address.region ? `, ${address.region}` : ''} {address.postalCode}
      </span>
      <br />
      <span>{address.countryCode}</span>
    </>
  )
}

/** S6/UID-100 (D2): renders the frozen as-placed `PaymentSelection` snapshot.
 * `displayLabel` may be null even for card/ach (a genuinely-missing display
 * label, not an error) — falls back to a generic kind label rather than
 * leaving the cell blank. */
/** UID-130 follow-up: lead with the payment METHOD TYPE (Credit card / ACH / Paid with PO),
 * then the display detail (masked last4 for card/ACH, the PO number for a PO invoice) — the field
 * previously showed only the bare detail (e.g. "•••• 4242") with no indication of the method type.
 * UID-317 review follow-up: render the method type and its detail on TWO lines (design's
 * `payTop`/`payBottom`, `Fictiv Platform.dc.html:3886`) instead of one " · "-joined string that
 * wrapped mid-token (e.g. "Purchase order · PO-DEMO-\nPAY-6"). PO now reads "Paid with PO" over the
 * PO number, matching the mock. */
function formatPaymentMethod(payment: Order['payment']): ReactNode {
  if (!payment) {
    return <span className="caption-text">—</span>
  }
  let top: string
  let detail: string | null
  if (payment.kind === 'card') {
    top = 'Credit card'
    detail = payment.displayLabel ?? null
  } else if (payment.kind === 'ach') {
    top = 'ACH'
    detail = payment.displayLabel ?? null
  } else {
    top = 'Paid with PO'
    detail = payment.poNumber
  }
  return (
    <>
      <span>{top}</span>
      {detail && (
        <>
          <br />
          <span>{detail}</span>
        </>
      )}
    </>
  )
}

/** Renders the "Shipping terms" field (shown on EVERY order — a general shipping field, not
 * import-only). DAP-vs-DDP is an IMPORT distinction keyed on the parts' COUNTRY OF ORIGIN via the
 * shared {@link isImportCustoms} predicate (NOT on mere customs-record presence — every order
 * carries a record as of UID-232). An import → "DDP (Fictiv is importer)"; a DOMESTIC order (US
 * origin, or origin not yet known — every live-placed order today) ships under DAP (Delivered At
 * Place) and the buyer never incurs customs. */
function formatShippingTerms(customs: Order['customs']): ReactNode {
  if (customs != null && isImportCustoms(customs)) {
    return `${customs.incoterms} (Fictiv is importer)`
  }
  return 'DAP'
}

/** UID-212: the order-detail "Reference quote #". Lineage is LINE-LEVEL
 * (migration 3012) — an order can span lines priced from different quotes — so
 * this collapses the distinct non-null `sourceQuoteId`s across the order's lines
 * into the prototype's single "Reference quote #" text field
 * (Fictiv Platform.dc.html:3318): one distinct quote shows it, several show a
 * comma-separated list, and none keeps the honest "None" (a catalog-only order,
 * or any pre-3012 order, genuinely has no originating quote — never guessed).
 *
 * Each id is rendered through `formatQuoteRef` as a human-readable "Q-#####"
 * (mirroring the "SO-#####" order ref) — quote ids are UUIDs in real data, so
 * showing the raw value would repeat the very bare-UUID problem this order card
 * otherwise avoids. */
function formatReferenceQuote(lines: Order['lines']): ReactNode {
  const refs = [
    ...new Set(lines.map((line) => line.sourceQuoteId).filter((id): id is string => id != null)),
  ]
  if (refs.length === 0) {
    return <span className="caption-text">None</span>
  }
  // UID-317 review follow-up: when an order spans MORE THAN ONE quote, stack the
  // refs on separate lines instead of a comma-joined string that widened/expanded
  // the grid column. A single quote stays on one line.
  return refs.map((id, index) => (
    <span key={id}>
      {index > 0 && <br />}
      {formatQuoteRef(id)}
    </span>
  ))
}

export function OrderSummaryCardSkeleton() {
  return (
    <Card className="order-summary-card" aria-hidden="true">
      <div className="order-summary-primary-row">
        <div className="skeleton" style={{ width: '18%', height: 16 }} />
        <div className="skeleton" style={{ width: '18%', height: 16 }} />
        <div className="skeleton" style={{ width: '18%', height: 16 }} />
      </div>
      <div className="order-summary-detail-row">
        <div className="skeleton" style={{ width: '70%', height: 16 }} />
        <div className="skeleton" style={{ width: '50%', height: 16 }} />
      </div>
    </Card>
  )
}
